import React, { useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { toast } from "sonner";
import { Mail, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success("Check your email for the reset link");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f8fafc]">
      <Header />
      <main className="flex-1 grid place-items-center px-6 py-16">
        <div className="w-full max-w-md bg-white border border-[#e2e8f0] rounded-lg p-8" data-testid="forgot-password-card">
          <Link to="/login" data-testid="link-back-to-login" className="inline-flex items-center gap-1.5 text-xs text-[#475569] hover:text-[#0f1b2d] mb-4">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to login
          </Link>
          <h1 className="font-display font-bold text-2xl text-[#0f1b2d]">Reset your password</h1>
          {sent ? (
            <div className="mt-6 space-y-4" data-testid="forgot-sent-state">
              <div className="flex items-start gap-3 p-4 rounded-md bg-emerald-50 border border-emerald-200">
                <Mail className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-emerald-900">
                  <div className="font-semibold">Check your inbox</div>
                  <div className="mt-1">If an account exists for <span className="font-medium">{email}</span>, we&apos;ve sent a password reset link. It will expire in 1 hour.</div>
                </div>
              </div>
              <button
                onClick={() => { setSent(false); setEmail(""); }}
                data-testid="forgot-send-another"
                className="w-full text-sm text-[#2563eb] hover:text-[#1d4ed8] font-semibold py-2"
              >
                Send to another email
              </button>
            </div>
          ) : (
            <>
              <p className="mt-1 text-sm text-[#475569]">Enter your account email and we&apos;ll send you a link to reset your password.</p>
              <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="forgot-form">
                <div>
                  <label className="text-sm font-semibold text-[#0f1b2d]">Email</label>
                  <input
                    type="email"
                    required
                    data-testid="forgot-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1.5 w-full border border-[#e2e8f0] rounded-md px-3 py-2.5 text-sm outline-none focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="forgot-submit-btn"
                  className="w-full bg-[#2563eb] hover:bg-[#1d4ed8] disabled:opacity-60 text-white font-semibold py-2.5 rounded-md transition-all"
                >
                  {submitting ? "Sending…" : "Send reset link"}
                </button>
              </form>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
